<template>
  <div>
    <Line :data="chartData" :options="chartOptions" />
  </div>
</template>
<script setup>
const { locale } = useI18n();
import { useDisplay } from 'vuetify';
const { startYear } = usePathwaysConfig();
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { ref } from "vue";
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const props = defineProps({
  chartData: Object,
  chartUnits: String,
  title: String,
  highlightYear: Number,
  stacked: {
    type: Boolean,
    default: false,
  },
  aspectRatio: {
    type: Number,
    default: 2,
  },
  mobileAspectRatio: {
    type: Number,
    default: 1,
  },
  max: Number,
  min: Number,
  suggestedMax: Number,
  suggestedMin: Number,
});

const { mobile } = useDisplay();

const yearIndex = ref(props.highlightYear - startYear);
watch(
  () => props.highlightYear,
  () => {
    yearIndex.value = props.highlightYear - startYear;
    setChartOptions();
  }
);

const highlightPoint = (context) => {
  if (context.dataIndex === yearIndex.value) {
    return 6; // Enlarge the point radius for the selected year
  }
  return 3; // Use the default point radius for other years
};

const chartOptions = ref(null);

const setChartOptions = () => {
  chartOptions.value = {
    responsive: true,
    aspectRatio: mobile.value ? props.mobileAspectRatio : props.aspectRatio,
    locale: locale.value,
    maintainAspectRatio: true,
    plugins: {
      title: {
        display: true,
        text: props.title,
        color: "#CB5546",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return context.dataset.label + ": " + context.formattedValue + " " + props.chartUnits; // Display the units
          },
        },
      },
      hover: {
        mode: "nearest",
        intersect: true,
      },
    },
    scales: {
      y: {
        stacked: props.stacked ? true : false,
        beginAtZero: props.stacked ? false : true,
        grid: {
          display: false,
        },
      },
    },
    elements: {
      point: {
        radius: highlightPoint,
      },
    },
  };

  // add suggested max and/or min if provided
  ["max", "min", "suggestedMax", "suggestedMin"].forEach((prop) => {
    if (props[prop]) {
      const axis = 'y';
      if (!(axis in chartOptions.value.scales)) {
        chartOptions.value.scales[axis] = {};
      }
      chartOptions.value.scales[axis][prop] = props[prop];
    }
  });
};

onBeforeMount(() => {
  setChartOptions();
});
</script>
